import React, { useMemo, useState } from 'react';
import './GroupFacilitiesModal.css';

function normalizeId(id) {
  if (id === null || id === undefined) return null;
  const s = id.toString();
  const n = parseFloat(s);
  if (Number.isFinite(n)) return Math.round(n).toString();
  return s;
}

function GroupFacilitiesModal({ groupName, hierarchy, facilityIds, onClose }) {
  const [search, setSearch] = useState('');

  const includedSet = useMemo(() => {
    const s = new Set();
    (facilityIds || []).forEach(id => {
      const norm = normalizeId(id);
      if (norm) s.add(norm);
      s.add(id.toString());
    });
    return s;
  }, [facilityIds]);

  const subtreeHasIncluded = (node) => {
    if (node.vitalEngine_Facility_ID__c) {
      const norm = normalizeId(node.vitalEngine_Facility_ID__c);
      if (norm && includedSet.has(norm)) return true;
    }
    if (node.children?.length) return node.children.some(subtreeHasIncluded);
    return false;
  };

  const nodeSelfMatches = (node) => {
    if (!search) return true;
    const s = search.toLowerCase();
    if (node.name?.toLowerCase().includes(s)) return true;
    if (node.billingCity?.toLowerCase().includes(s)) return true;
    if (node.billingState?.toLowerCase().includes(s)) return true;
    if (node.vitalEngine_Facility_ID__c?.toString().includes(s)) return true;
    return false;
  };

  const matchesSearch = (node) => {
    if (!search) return true;
    if (nodeSelfMatches(node)) return true;
    if (node.children?.length) return node.children.some(matchesSearch);
    return false;
  };

  const countIncludedLeaves = (node) => {
    let count = 0;
    const norm = normalizeId(node.vitalEngine_Facility_ID__c);
    const isLeaf = !node.children?.length;
    if (isLeaf && norm && includedSet.has(norm)) count += 1;
    if (node.children?.length) {
      node.children.forEach(c => { count += countIncludedLeaves(c); });
    }
    return count;
  };

  const totalIncluded = useMemo(() => {
    return hierarchy.reduce((acc, n) => acc + countIncludedLeaves(n), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchy, includedSet]);

  const renderNode = (node, level = 0) => {
    if (!subtreeHasIncluded(node)) return null;
    if (!matchesSearch(node)) return null;

    const norm = normalizeId(node.vitalEngine_Facility_ID__c);
    const selfIncluded = norm && includedSet.has(norm);
    const hasChildren = node.children?.some(subtreeHasIncluded);

    return (
      <div key={node.id} className="gfm-node" style={{ paddingLeft: `${level * 1.25}rem` }}>
        <div className={`gfm-row ${selfIncluded ? 'included' : 'ancestor'}`}>
          <span className="gfm-bullet">{hasChildren ? '▾' : '•'}</span>
          <span className="gfm-name">{node.name}</span>
          {node.vitalEngine_Facility_ID__c && (
            <span className="gfm-id">ID: {Math.round(parseFloat(node.vitalEngine_Facility_ID__c))}</span>
          )}
          {(node.billingCity || node.billingState) && (
            <span className="gfm-loc">
              {[node.billingCity, node.billingState].filter(Boolean).join(', ')}
            </span>
          )}
          {selfIncluded && <span className="gfm-pill">included</span>}
        </div>
        {hasChildren && node.children.map(c => renderNode(c, level + 1))}
      </div>
    );
  };

  const anyRoots = hierarchy.some(subtreeHasIncluded);

  return (
    <div className="gfm-overlay" onClick={onClose}>
      <div className="gfm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gfm-header">
          <div>
            <h3>Facilities in {groupName}</h3>
            <p className="gfm-subtitle">
              {totalIncluded} facilit{totalIncluded === 1 ? 'y' : 'ies'} · showing the hierarchy path to each
            </p>
          </div>
          <button className="gfm-close" onClick={onClose} title="Close">×</button>
        </div>

        <div className="gfm-search">
          <input
            type="text"
            placeholder="Filter by name, ID, city, or state…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="gfm-clear-search" onClick={() => setSearch('')}>×</button>
          )}
        </div>

        <div className="gfm-tree">
          {!anyRoots && (
            <div className="gfm-empty">No facilities are included in this group.</div>
          )}
          {anyRoots && hierarchy.map(n => renderNode(n))}
        </div>

        <div className="gfm-footer">
          <button className="gfm-done" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export default GroupFacilitiesModal;
